import React from "react";
import { StoryblokBlockComponent } from "../../lib/storyblok/types/StoryblokComponent";
import ComparisonTableUi from "../../ui/ComparisonTable";
import { ComparisonTableBlock } from "../../lib/storyblok/types/blocks/ComparisonTableBlock";
import Button from "../../ui/Button";
import { useLinks } from "../../lib/storyblok/useLinks";
import { useContentStyle, useButtonStyle } from "../../hooks/useStyle";
import { isValidImageData, imageSizes } from "../../lib/utils/imageUtils";
import StoryblokImage from "../StoryblokImage";
import { storyblokEditable, SbBlokData } from "@storyblok/react";

const ComparisonTable: StoryblokBlockComponent<ComparisonTableBlock> = ({
  title,
  description,
  buttons,
  innerSpacing,
  outerSpacing,
  style,
  highlightedStyle,
  highlightedColumn,
  header,
  rows,
}) => {
  const resolveLink = useLinks();
  const isInverted = useContentStyle(style);
  const { buttonVariant } = useButtonStyle(style);

  const headerData = Array.isArray(header) ? header[0] : header;
  const headerColumns = headerData?.columns?.map((column) => (
    <ComparisonTableUi.HeaderColumn
      key={column._uid}
      label={column.label}
      title={column.title}
      description={column.description}
      image={
        column.image && isValidImageData(column.image) ? (
          <StoryblokImage
            image={column.image}
            layout="fill"
            sizes={imageSizes("100vw", { tablet: "320px" })}
            resize={{ width: 320 }}
          />
        ) : undefined
      }
      buttons={column.buttons?.map((button) => (
        <Button
          variant={
            highlightedColumn &&
            parseInt(highlightedColumn, 10) !== 0 &&
            parseInt(highlightedColumn, 10) ===
              headerData?.columns?.indexOf(column) + 1
              ? "inverted"
              : button.buttonType || buttonVariant || "default"
          }
          key={button._uid}
          href={button.buttonLink ? resolveLink(button.buttonLink) : ""}
          size={
            highlightedColumn &&
            parseInt(highlightedColumn, 10) ===
              headerData?.columns?.indexOf(column) + 1
              ? "medium"
              : "small"
          }
        >
          {button.buttonText}
        </Button>
      ))}
      {...storyblokEditable(column as SbBlokData)}
    />
  ));

  const tableRows = rows?.map((row) => {
    if (row.component === "comparisonTableSection") {
      return (
        <ComparisonTableUi.Section
          key={row._uid}
          title={row.title}
          {...storyblokEditable(row as SbBlokData)}
        />
      );
    }

    if (row.component === "comparisonTableRow") {
      return (
        <ComparisonTableUi.Row
          key={row._uid}
          feature={row.feature}
          {...storyblokEditable(row as SbBlokData)}
        >
          {row.columns?.map((column) => (
            <ComparisonTableUi.RowColumn
              key={column._uid}
              showStatus={column.showStatus}
              status={column.status}
              title={column.title}
              description={column.description}
              {...storyblokEditable(column as SbBlokData)}
            />
          ))}
        </ComparisonTableUi.Row>
      );
    }

    return null;
  });

  return (
    <ComparisonTableUi
      title={title}
      description={description}
      buttons={buttons?.map((button) => (
        <Button
          variant={button.buttonType || buttonVariant || "default"}
          key={button._uid}
          href={button.buttonLink ? resolveLink(button.buttonLink) : ""}
        >
          {button.buttonText}
        </Button>
      ))}
      style={style}
      innerSpacing={innerSpacing}
      outerSpacing={outerSpacing}
      inverted={isInverted}
      highlightedColumn={
        highlightedColumn ? parseInt(highlightedColumn, 10) : undefined
      }
      highlightedStyle={highlightedStyle}
    >
      {headerData && headerData.columns && headerData.columns.length > 0 && (
        <ComparisonTableUi.Header>{headerColumns}</ComparisonTableUi.Header>
      )}

      {tableRows}
    </ComparisonTableUi>
  );
};

export default ComparisonTable;
